import React from 'react'
import clsx from 'clsx'
import AddChannel from '../../EHAccordion/ProfileV2/AddChannel'
import {
  Copy,
  Edit2,
  MinusCirlce,
  More,
  TickCircle,
  Trash,
  Warning2
} from 'iconsax-react'
import ButtonMenu from '../../ButtonMenu2'
import OverflowText from '../../../atoms/OverflowText'
import Tooltip from '../../../atoms/Tooltip'
import PhoneFlag from '../../../atoms/PhoneFlag'
import { useTheme } from '@mui/material'

import { useCopyableState } from '../../../../providers/CopyableStateProvider'
import { getFormatted } from '../../../../utilities/phoneNumber'
import useStyles from '../styles'

const DeleteIcon = React.memo(() => (
  <Trash size='14' color='#8F8F8F' variant='Bold' style={{ marginRight: 8 }} />
))
const CopyIcon = React.memo(() => (
  <Copy size='14' color='#8F8F8F' variant='Bold' style={{ marginRight: 8 }} />
))

const EditIcon = React.memo(() => (
  <Edit2 size='14' color='#8F8F8F' variant='Bold' style={{ marginRight: 8 }} />
))

const optedOutText = 'User opted out from using this communication channel'
const conflictText = 'Communication channel is used by another user'

interface ChannelRowProps {
  user: APIUser
  channel: APIChannel
  editingChannel: APIChannel
  showOptions?: boolean
  canDelete?: boolean
  showVerifiedIcon?: boolean
  canChangeChannel?: boolean
  isPhone?: boolean
  onChannelAdd: () => void
  setEditingChannel: (channel: null | APIChannel) => void
  handleOnChannelClick: (
    channel: APIChannel,
    isPhone: boolean,
    isVerified: boolean
  ) => void
  onDelete?: (APIChannel) => void
}

const ChannelRow: React.FC<ChannelRowProps> = ({
  user,
  channel,
  editingChannel,
  showOptions,
  canDelete,
  showVerifiedIcon,
  canChangeChannel,
  isPhone,
  onChannelAdd,
  setEditingChannel,
  handleOnChannelClick,
  onDelete
}) => {
  const { setText, copyToClipboard } = useCopyableState()
  const theme = useTheme()
  const classes = useStyles({ canChangeChannel, onDelete: canDelete })

  const valid = !isPhone
    ? channel.validated && channel.compliance
    : channel.validated && channel.compliance && channel.type !== 'voice_only'

  const isEditing = editingChannel?.id === channel?.id

  const options = React.useMemo(() => {
    const options = [
      {
        name: 'Copy value',
        Icon: CopyIcon
      },
      {
        name: 'Edit',
        Icon: EditIcon
      }
    ]
    if (canDelete) {
      options.push({
        name: 'Delete',
        Icon: DeleteIcon
      })
    }
    return options
  }, [canDelete])

  const handleMenuSelect = React.useCallback(
    option => {
      if (option === 'Copy value') {
        setText(isPhone ? getFormatted(channel.value) : channel.value)

        copyToClipboard()
        return
      }
      if (option === 'Delete') {
        onDelete(channel)
        return
      }
      setEditingChannel(channel)
    },
    [channel, isPhone, onDelete, setEditingChannel, setText, copyToClipboard]
  )

  if (isEditing)
    return (
      <AddChannel
        channels={user?.channels}
        channel={editingChannel}
        onCancel={() => {
          setEditingChannel(null)
        }}
        onAdd={() => {
          onChannelAdd()
          setEditingChannel(null)
        }}
        canDelete={canDelete}
        isPhone={isPhone}
      />
    )

  return (
    <div
      className={classes.channelRow}
      onClick={() => {
        handleOnChannelClick(channel, Boolean(isPhone), valid)
      }}
      style={{
        cursor: !valid && 'default'
      }}
    >
      <OverflowText className={classes.channelName}>
        {channel.name}
      </OverflowText>

      {isPhone && <PhoneFlag value={channel.value} className={classes.flag} />}

      <OverflowText className={classes.channelValue}>
        {isPhone ? getFormatted(channel.value) : channel.value}
      </OverflowText>

      {!channel.compliance && (
        <Tooltip title={optedOutText} arrow>
          <MinusCirlce
            variant='Bold'
            size={theme.size.base}
            className={clsx(classes.channelEndIcon, classes.optedOutIcon)}
          />
        </Tooltip>
      )}
      {showVerifiedIcon && channel.validated && (
        <TickCircle
          size={theme.size.base}
          variant='Bold'
          color='#06C270'
          className={classes.statusIcon}
        />
      )}
      {showVerifiedIcon && !channel.validated && (
        <Tooltip title={conflictText} arrow>
          <Warning2
            size={theme.size.base}
            variant='Bold'
            color='#FFC600'
            className={classes.statusIcon}
          />
        </Tooltip>
      )}
      {showOptions && (
        //@ts-ignore
        <ButtonMenu
          className={classes.buttonMenuClass}
          options={options}
          onSelect={handleMenuSelect}
        >
          <More
            size={theme.size.base}
            className={clsx(classes.icon, classes.trashIcon)}
          />
        </ButtonMenu>
      )}
    </div>
  )
}

export default ChannelRow
